import React, { useContext } from "react";

import {
  Assignment as AssignmentIcon,
  FileCopy as FileCopyIcon,
} from "@mui/icons-material";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

import OverwriteColumnDialog from "./Dialogs/OverwriteColumnDialog";
import { Column } from "../store/Column";
import MealPlanFormStore from "../store/MealPlanFormStore";

interface Props {
  column: Column;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3, 4),
  },
  mealPattern: {
    backgroundColor: "#FBB631",
    color: "#fafafa",
    "& .MuiTypography-root": {
      color: "#fafafa",
    },
    "& .totalEnergy": {
      color: "#fafafa",
    },
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
  },
  headIcon: {
    color: "#C0C5CD",
  },
  date: {
    fontWeight: "bold",
  },
  nutrients: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  energy: {
    display: "flex",
  },
  totalEnergy: {
    color: colors.gray,
  },
  totalEnergyReached: {
    color: "#FF6262",
  },
  totalEnergyLight: {
    color: "#fafafa",
  },
  macronutrients: {
    display: "flex",
  },
  macro: {
    marginRight: theme.spacing(3),
  },
}));

const ColumnHeader: React.FC<Props> = observer(({ column }) => {
  const { classes, cx } = useStyles();

  const store = useContext(MealPlanFormStore.Context);
  const recommendedCalories = store.recommendedCalories;
  const macronutrients = store.macronutrients;
  const clipboard = store.clipboard;
  const canCopy =
    column.mode !== "meal_pattern" && !clipboard.isCopying && !column.isEmpty;
  const canPaste =
    column.mode !== "meal_pattern" &&
    clipboard.mode === "column" &&
    clipboard.copiedColumn !== column;
  const overwriteColumnDialog = useDialogState();
  const confirmPasteColumn = () => {
    clipboard.pasteColumn(column);
  };
  const askPasteColumn = () => {
    if (!column.isEmpty) {
      overwriteColumnDialog.show();
    } else {
      confirmPasteColumn();
    }
  };

  return (
    <>
      <div
        className={cx(
          classes.root,
          column.mode === "meal_pattern" && classes.mealPattern
        )}
      >
        <div className={classes.head}>
          {column.mode === "meal_pattern" && (
            <div>
              <Typography className={classes.date}>{column.label}</Typography>
              <Typography variant="caption">
                Day 1 - Day {store.columns.durationDays}
              </Typography>
            </div>
          )}
          {column.mode === "date" && (
            <div>
              <Typography className={classes.date}>{column.label}</Typography>
              <Typography variant="caption" color="textSecondary">
                {format(column.date, "EEEE")}
              </Typography>
            </div>
          )}
          {column.mode === "day" && (
            <Typography className={classes.date}>{column.label}</Typography>
          )}
          <span>
            {canCopy && (
              <Tooltip title="Copy">
                <IconButton
                  className={classes.headIcon}
                  size="small"
                  onClick={() => clipboard.copyColumn(column)}
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canPaste && (
              <Tooltip title="Paste">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={askPasteColumn}
                >
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </span>
        </div>
        <div className={classes.nutrients}>
          <div className={classes.energy}>
            <Typography variant="subtitle1" color="textPrimary">
              <span
                className={cx(classes.totalEnergy, {
                  [classes.totalEnergyLight]: column.mode === "meal_pattern",
                  [classes.totalEnergyReached]: column.isCalorieLimitReached,
                })}
              >
                {Math.round(column.totalEnergyKcal)}
              </span>{" "}
              / {recommendedCalories} kcal
            </Typography>
          </div>
          {!!macronutrients && (
            <div className={classes.macronutrients}>
              <Typography
                className={classes.macro}
                variant="subtitle1"
                color="textSecondary"
              >
                C: {macronutrients.carbohydrates}%
              </Typography>

              <Typography
                className={classes.macro}
                variant="subtitle1"
                color="textSecondary"
              >
                P: {macronutrients.proteins}%
              </Typography>

              <Typography
                className={classes.macro}
                variant="subtitle1"
                color="textSecondary"
              >
                F: {macronutrients.fats}%
              </Typography>
            </div>
          )}
        </div>
      </div>
      <OverwriteColumnDialog
        column={column}
        open={overwriteColumnDialog.shown}
        onClose={overwriteColumnDialog.hide}
        onConfirm={confirmPasteColumn}
      />
    </>
  );
});

export default ColumnHeader;
